import React from 'react';
import styles from '../../../styles/CounsellingManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

export interface CounsellingRecord {
  id: string;
  student?: string;
  student_registration_number?: string;
  student_name: string;
  student_display_name?: string;
  academic_year?: number | string;
  academic_year_name?: string;
  year: string;
  semester: string;
  transport_mode?: string;
  journey_time?: string;
  have_textbooks?: string;
  difficult_subjects?: string;
  difficult_subjects_reason?: string;
  easy_subjects?: string;
  easy_subjects_reason?: string;
  attendance_marks?: Array<{
    subject: string;
    mid1: string;
    online1: string;
    mid2: string;
    online2: string;
  }>;
  attendance_months?: Array<{
    month: string;
    percent: string;
  }>;
  remedial_suggestions?: string;
  study_hours?: string;
  record_hours?: string;
  entertainment_hours?: string;
  counselling_suggestions?: string;
  best_friends?: string;
  parent_interaction?: string;
  faculty_encouragement?: string;
  disturbances?: string;
  counsellor_comments?: Array<{
    date: string;
    comment: string;
  }>;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  created_by_name?: string;
}

interface CounsellingCardProps {
  counselling: CounsellingRecord;
  onClick: (counselling: CounsellingRecord) => void;
  onEdit?: (counselling: CounsellingRecord) => void;
  onDelete?: (counselling: CounsellingRecord) => void;
  onDownload?: (counselling: CounsellingRecord) => void;
  showEditDelete?: boolean;
}

const CounsellingCard: React.FC<CounsellingCardProps> = ({
  counselling,
  onClick,
  onEdit,
  onDelete,
  onDownload,
  showEditDelete = false
}) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDescription = () => {
    if (counselling.counsellor_comments && Array.isArray(counselling.counsellor_comments) && counselling.counsellor_comments.length > 0) {
      const lastComment = counselling.counsellor_comments[counselling.counsellor_comments.length - 1];
      const commentText = lastComment.comment || '';
      return commentText.length > 100
        ? commentText.substring(0, 100) + '...'
        : commentText;
    }
    if (counselling.remedial_suggestions) {
      return counselling.remedial_suggestions.length > 100
        ? counselling.remedial_suggestions.substring(0, 100) + '...'
        : counselling.remedial_suggestions;
    }
    return 'No comments provided';
  };

  return (
    <div className={styles.counsellingCard} onClick={() => onClick(counselling)}>
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div
            className={styles.counsellingIconWrapper}
            style={{
              backgroundColor: '#EFF6FF',
              color: '#3B82F6'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className={styles.cardTitleSection}>
            <h3 className={styles.cardTitle}>{counselling.student_display_name || counselling.student_name || 'Student Counselling'}</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
              {counselling.student_registration_number && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray)' }}>
                  Reg. No: {counselling.student_registration_number}
                </div>
              )}
              {counselling.academic_year_name && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--primary)', fontWeight: 500 }}>
                  {counselling.academic_year_name}
                </div>
              )}
            </div>
            <div className={styles.cardDescription}>
              {getDescription()}
            </div>
          </div>
        </div>
        <div className={styles.cardHeaderRight}>
          {showEditDelete && (onEdit || onDelete) && (
            <div className={styles.cardActions}>
              {onEdit && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(counselling);
                  }}
                  title="Edit Counselling"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              )}
              {onDownload && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(counselling);
                  }}
                  title="Download Report"
                  style={{ color: '#10B981', borderColor: '#D1FAE5', backgroundColor: '#ECFDF5' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(counselling);
                  }}
                  title="Delete Counselling"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Body - Tags and Badges */}
      <div className={styles.cardBody}>
        <div className={styles.cardTags}>
          {/* Year & Semester Badge */}
          <div
            className={styles.categoryTag}
            style={{
              backgroundColor: '#EFF6FF',
              color: '#3B82F6',
              border: '1px solid #DBEAFE'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span style={{ fontSize: '0.625rem', fontWeight: '500' }}>
              Year {counselling.year} - Sem {counselling.semester}
            </span>
          </div>

          {/* Difficult Subjects Badge */}
          {counselling.difficult_subjects && (
            <div
              className={styles.categoryTag}
              style={{
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FECACA'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span style={{ fontSize: '0.625rem' }}>Difficult: {counselling.difficult_subjects}</span>
            </div>
          )}

          {/* Easy Subjects Badge */}
          {counselling.easy_subjects && (
            <div
              className={styles.categoryTag}
              style={{
                backgroundColor: '#F0FDF4',
                color: '#16A34A',
                border: '1px solid #BBF7D0'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span style={{ fontSize: '0.625rem' }}>Easy: {counselling.easy_subjects}</span>
            </div>
          )}

          {/* Attendance Badge */}
          {counselling.attendance_months && Array.isArray(counselling.attendance_months) && counselling.attendance_months.length > 0 && (
            <div
              className={styles.categoryTag}
              style={{
                backgroundColor: '#FEF3C7',
                color: '#D97706',
                border: '1px solid #FDE68A'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span style={{ fontSize: '0.625rem' }}>
                Attendance: {counselling.attendance_months.length} month{counselling.attendance_months.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Metadata Section */}
        <div className={styles.cardMetadata}>
          <div className={styles.metadataItem}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span className={styles.metadataText}>Created: {formatDate(counselling.created_at)}</span>
          </div>
          {counselling.updated_at && counselling.updated_at !== counselling.created_at && (
            <div className={styles.metadataItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span className={styles.metadataText}>Updated: {formatDate(counselling.updated_at)}</span>
            </div>
          )}
          {counselling.created_by_name && (
            <div className={styles.metadataItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span className={styles.metadataText}>By: {counselling.created_by_name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CounsellingCard;
